import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  Chart,
  IssueCardData,
  TrendFeedIssueItem,
} from 'features/trend-detection/types'
import { mapIssuesToCardData } from 'features/trend-detection/utils/helpers'

export interface UseFeedIssueChartsResult {
  feedIssueCards: IssueCardData[]
  chartDataMap: Record<string, Chart>
  chartLoadingMap: Record<string, boolean>
  projectTotalChartMap: Record<string, Chart>
}

export function useFeedIssueCharts(
  service: TrendDetectionService,
  projectId: string | null,
  issuesItems: TrendFeedIssueItem[],
): UseFeedIssueChartsResult {
  const itemsRef = useRef(issuesItems)
  itemsRef.current = issuesItems

  const runForIssue = useCallback((issueUuid: string): string | null => {
    return (
      itemsRef.current.find((it) => it.issue.uuid === issueUuid)?.run_uuid ??
      null
    )
  }, [])

  const [chartDataMap, setChartDataMap] = useState<Record<string, Chart>>({})
  const [chartLoadingMap, setChartLoadingMap] = useState<
    Record<string, boolean>
  >({})
  const [projectTotalChartMap, setProjectTotalChartMap] = useState<
    Record<string, Chart>
  >({})

  const chartDataRef = useRef(chartDataMap)
  chartDataRef.current = chartDataMap
  const chartLoadingRef = useRef(chartLoadingMap)
  chartLoadingRef.current = chartLoadingMap

  const feedIssueCards: IssueCardData[] = useMemo(() => {
    return issuesItems.map((it, idx) => {
      const base = mapIssuesToCardData([it.issue])[0]
      if (!base) {
        throw new Error(
          `mapIssuesToCardData returned no card for issue ${it.issue.uuid}`,
        )
      }
      return {
        ...base,
        title: base.title ?? '',
        original_index: idx,
      }
    })
  }, [issuesItems])

  const fetchTimeseriesChartsBatch = useCallback(
    (issueUuids: string[]): void => {
      if (!projectId || issueUuids.length === 0) return
      const byRun = new Map<string, string[]>()
      for (const u of issueUuids) {
        const r = runForIssue(u)
        if (!r) continue
        const list = byRun.get(r) ?? []
        list.push(u)
        byRun.set(r, list)
      }
      const loadingPatch = Object.fromEntries(
        issueUuids.map((u) => [u, true] as const),
      )
      setChartLoadingMap((prev) => ({ ...prev, ...loadingPatch }))
      void Promise.all(
        [...byRun.entries()].map(([runUuid, uuids]) =>
          service
            .fetchIssueTimeseriesChartsBatch(projectId, runUuid, uuids)
            .then(({ charts, projectTotalChart }) => {
              setChartDataMap((prev) => ({ ...prev, ...charts }))
              if (projectTotalChart) {
                setProjectTotalChartMap((prev) => ({
                  ...prev,
                  [runUuid]: projectTotalChart,
                }))
              }
            })
            .catch(() => {}),
        ),
      ).finally(() => {
        const clearPatch = Object.fromEntries(
          issueUuids.map((u) => [u, false] as const),
        )
        setChartLoadingMap((prev) => ({ ...prev, ...clearPatch }))
      })
    },
    [projectId, runForIssue, service],
  )

  useEffect(() => {
    if (!projectId || issuesItems.length === 0) return
    const needToLoad = issuesItems
      .map((it) => it.issue.uuid)
      .filter(
        (uuid) =>
          !!uuid &&
          !chartDataRef.current[uuid] &&
          !chartLoadingRef.current[uuid],
      )
    if (needToLoad.length > 0) {
      fetchTimeseriesChartsBatch(needToLoad)
    }
  }, [issuesItems, projectId, fetchTimeseriesChartsBatch])

  return {
    feedIssueCards,
    chartDataMap,
    chartLoadingMap,
    projectTotalChartMap,
  }
}
